import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/cjs/styles/prism";
import { SandboxPlan } from "../types";
import "./CompanyChatPanel.css";

export interface CompanyChatPanelProps {
    darkMode: boolean;
    companyInfo: { companyName?: string } | null;
    userId: string | null;
    sessionId?: string | null;
    fetchSession?: (sessionId: string) => Promise<{ messages: { role: string; content: string }[]; plan?: SandboxPlan | null } | null>;
    onSessionUpdate?: (sessionId: string, title: string) => void;
    onSaveSession?: (sessionId: string | null, data: {
        messages: { role: string; content: string }[];
        plan: SandboxPlan | null;
        title?: string;
    }) => Promise<string | null>;
    onInterviewCreated?: (interviewId: string) => void;
    onPlanUpdated?: (plan: SandboxPlan | null) => void;
    /** Bump to refetch session (e.g. after creating interview, to load cleared plan) */
    refreshTrigger?: number;
    /** List of existing interviews (id, title) for update_interview tool context */
    interviews?: { id: string; title: string }[];
}

interface ChatMessage {
    role: "user" | "assistant";
    content: string;
    toolCall?: string;
}

const getApiBase = () => {
    if (process.env.NODE_ENV === "production") {
        return process.env.REACT_APP_BACKEND_URL || "";
    }
    return process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
};

function getSessionTitle(
    plan: SandboxPlan | null,
    messages: { role: string; content: string }[],
    latestUserMessage?: string
): string {
    if (plan?.title) {
        const parts: string[] = [];
        if ((plan.interviewType || "coding") === "system-design") parts.push("System Design");
        else if (plan.language) parts.push(plan.language);
        if (plan.difficulty) parts.push(plan.difficulty);
        const suffix = parts.length > 0 ? ` (${parts.join(" · ")})` : "";
        return plan.title + suffix;
    }
    const firstUser = messages.find((m) => m.role === "user");
    const content = firstUser?.content || latestUserMessage;
    if (content) {
        const trimmed = content.trim();
        const truncated = trimmed.slice(0, 50);
        return truncated + (trimmed.length > 50 ? "…" : "");
    }
    return "New chat";
}

const CompanyChatPanel: React.FC<CompanyChatPanelProps> = ({
    darkMode,
    companyInfo,
    userId,
    sessionId,
    fetchSession,
    onSessionUpdate,
    onSaveSession,
    onInterviewCreated,
    onPlanUpdated,
    refreshTrigger,
    interviews = [],
}) => {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [currentToolCall, setCurrentToolCall] = useState<string | null>(null);
    const [currentPlan, setCurrentPlan] = useState<SandboxPlan | null>(null);
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(sessionId ?? null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const prevLoadingRef = useRef(false);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, currentToolCall]);

    useEffect(() => {
        if (prevLoadingRef.current && !isLoading) {
            inputRef.current?.focus();
        }
        prevLoadingRef.current = isLoading;
    }, [isLoading]);

    // Load session when sessionId changes
    useEffect(() => {
        setCurrentSessionId(sessionId ?? null);
        if (!sessionId || !fetchSession) {
            setMessages([]);
            setCurrentPlan(null);
            return;
        }
        let cancelled = false;
        fetchSession(sessionId).then((data) => {
            if (!cancelled && data) {
                setMessages(data.messages as ChatMessage[]);
                setCurrentPlan(data.plan ?? null);
            }
        });
        return () => { cancelled = true; };
    }, [sessionId, fetchSession, refreshTrigger]);

    const handleSend = async () => {
        const text = input.trim();
        if (!text || !userId || isLoading) return;

        const userMessage: ChatMessage = { role: "user", content: text };
        setMessages((prev) => [...prev, userMessage]);
        setInput("");
        setIsLoading(true);
        setTimeout(() => inputRef.current?.focus(), 0);

        try {
            const response = await fetch(`${getApiBase()}/api/ai/sandbox-agent`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    messages: [...messages, userMessage].map((m) => ({
                        role: m.role,
                        content: m.content,
                    })),
                    companyContext: {
                        companyName: companyInfo?.companyName,
                    },
                    userId,
                    stream: true,
                    currentPlan: currentPlan ? {
                        id: currentPlan.id,
                        title: currentPlan.title,
                        description: currentPlan.description,
                        language: currentPlan.language,
                        difficulty: currentPlan.difficulty,
                        problemStatement: currentPlan.problemStatement,
                        systemDesignProblem: currentPlan.systemDesignProblem,
                        systemDesignPrompt: currentPlan.systemDesignPrompt,
                        suggestedQuestions: currentPlan.suggestedQuestions,
                        deadlineAt: currentPlan.deadlineAt,
                    } : null,
                    interviews: interviews.map((i) => ({ id: i.id, title: i.title })),
                }),
            });

            if (!response.ok) {
                throw new Error("Failed to get response");
            }

            const reader = response.body?.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let streamingContent = "";
            let toolsUsed: string[] = [];
            let finalPlan: SandboxPlan | null = null;
            let finalResponse = "";

            if (reader) {
                setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split("\n");
                    buffer = lines.pop() || "";

                    for (const line of lines) {
                        if (line.startsWith("data: ")) {
                            const data = line.slice(6);
                            if (data === "[DONE]") continue;

                            try {
                                const parsed = JSON.parse(data);
                                if (parsed.type === "text" && parsed.content) {
                                    streamingContent += parsed.content;
                                    const contentToSet = streamingContent;
                                    setMessages((prev) => {
                                        const next = [...prev];
                                        const last = next[next.length - 1];
                                        if (last?.role === "assistant") {
                                            next[next.length - 1] = { ...last, content: contentToSet };
                                        }
                                        return next;
                                    });
                                } else if (parsed.type === "tool_call" && parsed.name) {
                                    if (!toolsUsed.includes(parsed.name)) toolsUsed.push(parsed.name);
                                    setCurrentToolCall(parsed.name);
                                } else if (parsed.type === "complete") {
                                    finalResponse = parsed.response || "";
                                    if (parsed.plan) {
                                        finalPlan = {
                                            id: parsed.plan.id || `plan-${Date.now()}`,
                                            sessionId: parsed.plan.sessionId || "default",
                                            interviewType: parsed.plan.interviewType || "coding",
                                            title: parsed.plan.title || "Untitled",
                                            description: parsed.plan.description || "",
                                            language: parsed.plan.language || "Python",
                                            difficulty: parsed.plan.difficulty || "Medium",
                                            problemStatement: parsed.plan.problemStatement,
                                            systemDesignProblem: parsed.plan.systemDesignProblem,
                                            systemDesignPrompt: parsed.plan.systemDesignPrompt,
                                            suggestedQuestions: parsed.plan.suggestedQuestions,
                                            deadlineAt: parsed.plan.deadlineAt,
                                            status: parsed.plan.status || "draft",
                                            createdAt: parsed.plan.createdAt || new Date().toISOString(),
                                            updatedAt: parsed.plan.updatedAt || new Date().toISOString(),
                                        };
                                        setCurrentPlan(finalPlan);
                                        onPlanUpdated?.(finalPlan);
                                    }
                                    let content = finalResponse || (finalPlan ? `I've created a plan for "${finalPlan.title}".` : "");
                                    if (content && toolsUsed.length > 0) {
                                        content = `Used: ${toolsUsed.map((t) => t.replace(/_/g, " ")).join(", ")}\n\n${content}`;
                                    }
                                    if (content) {
                                        streamingContent = content;
                                        setMessages((prev) => {
                                            const next = [...prev];
                                            const last = next[next.length - 1];
                                            if (last?.role === "assistant") {
                                                next[next.length - 1] = { ...last, content };
                                            } else {
                                                next.push({ role: "assistant", content });
                                            }
                                            return next;
                                        });
                                    }
                                }
                            } catch {
                                // ignore parse errors
                            }
                        }
                    }
                }

                const assistantContent = streamingContent || finalResponse;
                if (assistantContent && onSaveSession) {
                    const newMessages = [...messages, userMessage, { role: "assistant" as const, content: assistantContent }];
                    const title = getSessionTitle(finalPlan, newMessages, userMessage.content);
                    const sid = await onSaveSession(currentSessionId, {
                        messages: newMessages,
                        plan: finalPlan,
                        title,
                    });
                    if (sid) {
                        setCurrentSessionId(sid);
                        onSessionUpdate?.(sid, title);
                    }
                }
            } else {
                // Fallback: non-streaming response
                const data = await response.json();
                if (data.toolCall) setCurrentToolCall(data.toolCall);

                const plan = data.plan
                    ? {
                          id: data.plan.id || `plan-${Date.now()}`,
                          sessionId: data.plan.sessionId || "default",
                          interviewType: data.plan.interviewType || "coding",
                          title: data.plan.title || "Untitled",
                          description: data.plan.description || "",
                          language: data.plan.language || "Python",
                          difficulty: data.plan.difficulty || "Medium",
                          problemStatement: data.plan.problemStatement,
                          systemDesignProblem: data.plan.systemDesignProblem,
                          systemDesignPrompt: data.plan.systemDesignPrompt,
                          suggestedQuestions: data.plan.suggestedQuestions,
                          deadlineAt: data.plan.deadlineAt,
                          status: data.plan.status || "draft",
                          createdAt: data.plan.createdAt || new Date().toISOString(),
                          updatedAt: data.plan.updatedAt || new Date().toISOString(),
                      }
                    : null;

                if (plan) {
                    setCurrentPlan(plan);
                    onPlanUpdated?.(plan);
                }

                const assistantContent = data.response || (plan ? `I've created a plan for "${plan.title}".` : "");
                if (assistantContent) {
                    const newMessages = [...messages, userMessage, { role: "assistant" as const, content: assistantContent }];
                    setMessages(newMessages);

                    if (onSaveSession) {
                        const title = getSessionTitle(plan, newMessages, userMessage.content);
                        const sid = await onSaveSession(currentSessionId, {
                            messages: newMessages,
                            plan,
                            title,
                        });
                        if (sid) {
                            setCurrentSessionId(sid);
                            onSessionUpdate?.(sid, title);
                        }
                    }
                }

                if (data.interviewId) {
                    onInterviewCreated?.(data.interviewId);
                }
            }
        } catch (err) {
            setMessages((prev) => {
                const last = prev[prev.length - 1];
                if (last?.role === "assistant" && last.content === "") {
                    return [...prev.slice(0, -1), { ...last, content: "Sorry, something went wrong. Please try again." }];
                }
                return [...prev, { role: "assistant" as const, content: "Sorry, something went wrong. Please try again." }];
            });
        } finally {
            setIsLoading(false);
            setCurrentToolCall(null);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleExecutePlan = async () => {
        if (!currentPlan || !userId || isLoading) return;

        setIsLoading(true);
        setCurrentToolCall("execute_plan");

        try {
            const response = await fetch(`${getApiBase()}/api/ai/sandbox-agent`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    messages: messages,
                    companyContext: { companyName: companyInfo?.companyName },
                    userId,
                    currentPlan: {
                        id: currentPlan.id,
                        interviewType: currentPlan.interviewType,
                        title: currentPlan.title,
                        description: currentPlan.description,
                        language: currentPlan.language,
                        difficulty: currentPlan.difficulty,
                        problemStatement: currentPlan.problemStatement,
                        systemDesignProblem: currentPlan.systemDesignProblem,
                        systemDesignPrompt: currentPlan.systemDesignPrompt,
                        suggestedQuestions: currentPlan.suggestedQuestions,
                        deadlineAt: currentPlan.deadlineAt,
                    },
                    action: "execute_plan",
                }),
            });

            const data = await response.json();

            if (data.interviewId) {
                setCurrentPlan(null);
                const newMsgs = [
                    ...messages,
                    {
                        role: "assistant" as const,
                        content: `Interview created! You can edit it from the list, send it to a candidate, or continue refining it in this chat.`,
                    },
                ];
                setMessages(newMsgs);
                if (onSaveSession) {
                    const title = getSessionTitle(null, newMsgs);
                    const sid = await onSaveSession(currentSessionId, {
                        messages: newMsgs,
                        plan: null,
                        title,
                    });
                    if (sid && !currentSessionId) {
                        setCurrentSessionId(sid);
                        onSessionUpdate?.(sid, title);
                    }
                }
                onInterviewCreated?.(data.interviewId);
            } else if (data.error) {
                setMessages((prev) => [
                    ...prev,
                    { role: "assistant", content: data.error },
                ]);
            }
        } catch (err) {
            setMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    content: "Failed to create interview. Please try again.",
                },
            ]);
        } finally {
            setIsLoading(false);
            setCurrentToolCall(null);
        }
    };

    const handleDeletePlan = async () => {
        setCurrentPlan(null);
        onPlanUpdated?.(null);
        if (onSaveSession && currentSessionId) {
            await onSaveSession(currentSessionId, {
                messages,
                plan: null,
            });
        }
    };

    return (
        <div className={`company-chat-panel ${darkMode ? "dashboard-dark" : ""}`}>
            <div className="company-chat-header">
                <h3>Interview Assistant</h3>
                <p>
                    Describe the interview you want to create. I'll help you set
                    it up.
                </p>
            </div>

            <div className="company-chat-scroll-area">
                <div className="company-chat-messages">
                {messages.length === 0 && !currentToolCall && (
                    <div className="company-chat-empty">
                        <p>Ask me to create or update an interview.</p>
                        <p>e.g. &quot;Create a Python interview for data structures&quot;</p>
                        <p>e.g. &quot;Make my URL shortener interview harder&quot;</p>
                        <p>e.g. &quot;Due by Friday&quot; or &quot;Invite jane@example.com, she has 5 days&quot;</p>
                    </div>
                )}
                {messages.map((msg, i) => {
                    const isLastEmptyAssistant = isLoading && !currentToolCall && i === messages.length - 1 && msg.role === "assistant" && !msg.content;
                    if (isLastEmptyAssistant) return null;
                    return (
                    <div
                        key={i}
                        className={`company-chat-message company-chat-markdown ${
                            msg.role === "user" ? "user" : "assistant"
                        }`}
                    >
                        <ReactMarkdown
                            components={{
                                code({ node, className, children, ...props }) {
                                    const match = /language-(\w+)/.exec(className || "");
                                    const content = String(children).replace(/\n$/, "");
                                    const isInline = !match && !content.includes("\n");

                                    if (isInline) {
                                        return (
                                            <code className={className} {...props}>
                                                {children}
                                            </code>
                                        );
                                    }
                                    return (
                                        <SyntaxHighlighter
                                            style={oneDark as Record<string, React.CSSProperties>}
                                            language={match ? match[1] : "text"}
                                            PreTag="div"
                                            customStyle={{
                                                margin: "0.5em 0",
                                                borderRadius: "6px",
                                                fontSize: "0.85em",
                                                padding: "0.8em",
                                            }}
                                        >
                                            {content}
                                        </SyntaxHighlighter>
                                    );
                                },
                            }}
                        >
                            {msg.content}
                        </ReactMarkdown>
                    </div>
                    );
                })}
                {isLoading && !currentToolCall && (() => {
                    const last = messages[messages.length - 1];
                    const waitingForFirstChunk = !last || (last.role === "user") || (last.role === "assistant" && !last.content);
                    return waitingForFirstChunk ? (
                        <div className="company-chat-message company-chat-loading">
                            <span className="company-chat-spinner" />
                            <span className="company-chat-loading-text">Thinking...</span>
                        </div>
                    ) : null;
                })()}
                {currentToolCall && (
                    <div className="company-chat-message tool-call">
                        Using: {currentToolCall.replace(/_/g, " ")}
                    </div>
                )}
                <div ref={messagesEndRef} />
                </div>

            {currentPlan && (
                <div className="company-chat-plan-preview">
                    <h4>Interview Plan</h4>
                    <div className="company-chat-plan-fields">
                        <label>
                            Title
                            <input
                                type="text"
                                value={currentPlan.title}
                                onChange={(e) =>
                                    setCurrentPlan((p) =>
                                        p ? { ...p, title: e.target.value } : null
                                    )
                                }
                                className="company-chat-plan-input"
                            />
                        </label>
                        <label>
                            Description
                            <textarea
                                value={currentPlan.description}
                                onChange={(e) =>
                                    setCurrentPlan((p) =>
                                        p ? { ...p, description: e.target.value } : null
                                    )
                                }
                                className="company-chat-plan-input"
                                rows={4}
                            />
                        </label>
                        <label>
                            Deadline (optional)
                            <input
                                type="date"
                                value={currentPlan.deadlineAt ? currentPlan.deadlineAt.slice(0, 10) : ""}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setCurrentPlan((p) =>
                                        p ? { ...p, deadlineAt: val || undefined } : null
                                    );
                                }}
                                className="company-chat-plan-input"
                            />
                        </label>
                        <div className="company-chat-plan-row">
                            <label>
                                Interview Type
                                <select
                                    value={currentPlan.interviewType || "coding"}
                                    onChange={(e) =>
                                        setCurrentPlan((p) =>
                                            p ? { ...p, interviewType: e.target.value as "coding" | "system-design" } : null
                                        )
                                    }
                                    className="company-chat-plan-input"
                                >
                                    <option value="coding">Coding</option>
                                    <option value="system-design">System Design</option>
                                </select>
                            </label>
                            {(currentPlan.interviewType || "coding") === "coding" && (
                                <label>
                                    Language
                                    <select
                                        value={currentPlan.language}
                                        onChange={(e) =>
                                            setCurrentPlan((p) =>
                                                p ? { ...p, language: e.target.value } : null
                                            )
                                        }
                                        className="company-chat-plan-input"
                                    >
                                        <option value="Python">Python</option>
                                        <option value="JavaScript">JavaScript</option>
                                        <option value="Java">Java</option>
                                        <option value="C++">C++</option>
                                    </select>
                                </label>
                            )}
                            <label>
                                Difficulty
                                <select
                                    value={currentPlan.difficulty}
                                    onChange={(e) =>
                                        setCurrentPlan((p) =>
                                            p ? { ...p, difficulty: e.target.value } : null
                                        )
                                    }
                                    className="company-chat-plan-input"
                                >
                                    <option value="Easy">Easy</option>
                                    <option value="Medium">Medium</option>
                                    <option value="Hard">Hard</option>
                                </select>
                            </label>
                        </div>
                        {(currentPlan.interviewType || "coding") === "system-design" ? (
                            <label>
                                System Design Problem
                                <textarea
                                    value={currentPlan.systemDesignProblem || ""}
                                    onChange={(e) =>
                                        setCurrentPlan((p) =>
                                            p ? { ...p, systemDesignProblem: e.target.value } : null
                                        )
                                    }
                                    className="company-chat-plan-input"
                                    rows={10}
                                    placeholder="e.g. Design a video streaming service like Netflix..."
                                />
                            </label>
                        ) : (
                            <label>
                                Problem Statement
                                <textarea
                                    value={currentPlan.problemStatement || ""}
                                    onChange={(e) =>
                                        setCurrentPlan((p) =>
                                            p ? { ...p, problemStatement: e.target.value } : null
                                        )
                                    }
                                    className="company-chat-plan-input"
                                    rows={10}
                                    placeholder="The coding challenge..."
                                />
                            </label>
                        )}
                    </div>
                </div>
            )}
            </div>

            {currentPlan && (
                <div className="company-chat-plan-actions-bar">
                    <button
                        className="company-chat-send"
                        onClick={handleExecutePlan}
                        disabled={isLoading}
                    >
                        Create Interview
                    </button>
                    <button
                        className="company-chat-send company-chat-delete"
                        onClick={handleDeletePlan}
                        disabled={isLoading}
                    >
                        Delete Plan
                    </button>
                </div>
            )}

            <div className="company-chat-input-wrap">
                <div className="company-chat-input-row">
                    <textarea
                        ref={inputRef}
                        className="company-chat-input"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Describe your interview..."
                        rows={1}
                        disabled={isLoading}
                    />
                    <button
                        className="company-chat-send"
                        onClick={handleSend}
                        disabled={isLoading || !input.trim()}
                    >
                        Send
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CompanyChatPanel;
